import { Calendar, CircleDollarSign, Hourglass } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';

interface ChannelInfoSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  icon: React.ReactNode;
  description: string;
  /** Pre-formatted duration string, e.g. "30 days" or "Min 7 days". Omit to hide the field. */
  displayDuration: string;
  /** Pre-formatted budget string, e.g. "$149" or "Min $300". Omit to hide the field. */
  displayBudget: string;
  channelType: string;
  processingTime?: string;
  locations?: string[];
  industries?: string[];
}

export function ChannelInfoSheet({
  open,
  onOpenChange,
  title,
  icon,
  description,
  displayDuration,
  displayBudget,
  channelType,
  processingTime,
  locations,
  industries,
}: ChannelInfoSheetProps) {
  const intl = useIntl();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="min-w-[464px]">
        <SheetHeader>
          <SheetTitle>
            <FormattedMessage
              defaultMessage="Channel details"
              description="Title of the modal for selecting openings for a job board"
            />
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-1 flex-col items-start gap-4 overflow-y-auto p-4">
          <div className="flex w-full items-center justify-between">
            {icon}
            <div className="flex items-start gap-2">
              {/* Duration */}
              {displayDuration != null && (
                <Info
                  title={intl.formatMessage({
                    defaultMessage: 'duration',
                    description: 'The duration of the channel',
                  })}
                  value={displayDuration}
                  icon={<Calendar className="text-fx-neutral-600 size-3" />}
                />
              )}

              {/* Budget */}
              {displayBudget != null && (
                <Info
                  title={intl.formatMessage({
                    defaultMessage: 'price',
                    description: 'The budget per opening of the channel',
                  })}
                  value={displayBudget}
                  icon={
                    <CircleDollarSign className="text-fx-neutral-600 size-3" />
                  }
                />
              )}
            </div>
          </div>

          {/* Channel type and title */}
          <div className="flex flex-col gap-1">
            <Typography variant="caption" className="text-fx-neutral-600">
              {channelType}
            </Typography>
            <Typography variant="medium2">{title}</Typography>
          </div>

          {/* Channel description */}
          <Typography variant="body1" className="text-fx-neutral-600">
            {description}
          </Typography>

          {/* Processing time */}
          {processingTime != null && (
            <Info
              title={intl.formatMessage({
                defaultMessage: 'processing time',
                description: 'The processing time of the channel',
              })}
              value={processingTime}
              icon={<Hourglass className="text-fx-neutral-600 size-3" />}
            />
          )}

          {/* Locations */}
          {locations != null && locations.length > 0 && (
            <Info
              title={intl.formatMessage({
                defaultMessage: 'locations',
                description: 'Header for the locations of the channel',
              })}
              value={locations}
            />
          )}

          {/* Industries */}
          {industries != null && industries.length > 0 && (
            <Info
              title={intl.formatMessage({
                defaultMessage: 'industries',
                description: 'Header for the industries of the channel',
              })}
              value={industries}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Info({
  title,
  value,
  icon,
}: {
  title: string;
  value: string | string[];
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-1">
      <Typography variant="caption" className="text-fx-neutral-600">
        {title}
      </Typography>
      {Array.isArray(value) ? (
        <div className="flex flex-wrap items-start gap-1">
          {value.map(item => (
            <Tag
              text={item}
              icon={icon}
              className="bg-fx-neutral-100"
              key={item}
            />
          ))}
        </div>
      ) : (
        <Tag text={value} icon={icon} className="bg-fx-neutral-100" />
      )}
    </div>
  );
}
